'use client';

import Image from 'next/image';
import { sectionHeadingTag } from '@/components/dynamic-zone/section-heading-level';
import { strapiImage } from '@/lib/strapi/strapiImage';
import { components } from '@/types/strapi';

export const FeatureBlocks = ({
	heading,
	sub_heading,
	blocks,
	isFirst = false,
}: components['schemas']['DynamicZoneFeatureBlocksSectionComponent'] & {
	isFirst?: boolean;
}) => {
	const SectionHeading = sectionHeadingTag(isFirst);
	if (!blocks || blocks.length === 0) {
		console.warn('No feature blocks provided');
		return null;
	}

	// Check if blocks have numbers to determine display style
	const hasNumbers = blocks.some(block => block.number);

	// Render with numbers in a different layout
	if (hasNumbers) {
		return (
			<section className="feature-blocks-section w-full bg-gray-50 py-16 md:py-24">
				<div className="container mx-auto px-8 md:px-6">
					{/* Heading Section */}
					<div className="mx-auto mb-16 max-w-3xl text-center">
						<SectionHeading className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
							{heading}
						</SectionHeading>
						{sub_heading && (
							<p className="mt-4 text-lg text-gray-600">
								{sub_heading}
							</p>
						)}
					</div>

					{/* Feature Blocks with Numbers */}
					<div
						className={`grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-4`}>
						{blocks.map((block, index) => (
							<div
								key={block.id || index}
								className="group relative flex flex-col">
								{/* Number Circle */}
								<div
									className="absolute left-4 top-6 z-10 flex h-16 w-16 -translate-y-1/2 items-center justify-center rounded-full text-lg font-bold text-white shadow-md transition-all duration-300 group-hover:scale-110 group-hover:shadow-lg"
									style={{
										backgroundColor:
											block.background_color || '#4F46E5',
									}}>
									{block.number ||
										(index + 1).toString().padStart(2, '0')}
								</div>

								{/* Card */}
								<div className="mt-6 flex h-full flex-col rounded-lg border border-gray-200 bg-white p-6 pt-12 shadow-sm transition-all duration-200 hover:shadow-md">
									{/* Icon */}
									{block.icon && block.icon.url && (
										<div className="mb-4 flex justify-center">
											<div className="relative h-12 w-12">
												<Image
													src={strapiImage(
														block.icon.url,
													)}
													alt={
														block.icon
															.alternativeText ||
														block.heading ||
														''
													}
													fill
													className="object-contain"
												/>
											</div>
										</div>
									)}

									{/* Content */}
									{block.heading && (
										<h3 className="mb-3 text-lg font-bold text-gray-900">
											{block.heading}
										</h3>
									)}
									{block.description && (
										<p className="text-md font-sans leading-relaxed text-gray-600">
											{block.description}
										</p>
									)}

									{/* CTA */}
									{block.CTA && (
										<div className="mt-auto pt-4">
											<a
												href={block.CTA.URL}
												target={
													block.CTA.target || '_self'
												}
												className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
												{block.CTA.text}
											</a>
										</div>
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			</section>
		);
	}

	// Normal blocks layout
	return (
		<section className="feature-blocks-section w-full bg-white py-8 md:py-12">
			<div className="container mx-auto px-4 md:px-10 lg:px-16">
				{/* Heading Section */}
				<div className="mx-auto mb-8 max-w-3xl text-center">
					<SectionHeading className="text-3xl font-bold tracking-tight text-gray-900 md:text-4xl">
						{heading}
					</SectionHeading>
					{sub_heading && (
						<p className="mt-3 text-lg text-gray-600">
							{sub_heading}
						</p>
					)}
				</div>

				{/* Feature Blocks Grid */}
				<div className="mx-auto max-w-7xl px-4 md:px-6">
					<div
						className={`grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-4`}>
						{blocks.map((block, index) => (
							<div
								key={block.id || index}
								className={`flex flex-col rounded-lg border border-gray-300 bg-white p-6 shadow-sm transition-all duration-300 hover:scale-[1.02] hover:border-indigo-200 hover:shadow-[0_0_20px_rgba(79,70,229,0.4)] ${
									block.background_color
										? `bg-[${block.background_color}]`
										: ''
								}`}>
								{/* Icon */}
								{block.icon && block.icon.url && (
									<div className="mb-4 flex justify-start">
										<div className="relative h-16 w-16">
											<Image
												src={strapiImage(
													block.icon.url,
												)}
												alt={
													block.icon
														.alternativeText ||
													block.heading ||
													''
												}
												fill
												className="object-contain"
											/>
										</div>
									</div>
								)}

								{/* Content */}
								{block.heading && (
									<h3 className="mb-4 text-xl font-bold uppercase tracking-wide text-gray-800">
										{block.heading}
									</h3>
								)}

								{block.description && (
									<p className="mb-6 text-gray-600">
										{block.description}
									</p>
								)}
								{/* CTA */}
								{block.CTA && (
									<div className="mt-auto flex justify-center">
										<a
											href={block.CTA.URL}
											target={block.CTA.target || '_self'}
											className="inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
											{block.CTA.text}
										</a>
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			</div>
		</section>
	);
};

export default FeatureBlocks;
